/**
 * useUpdateMenu hook.
 */
import Features from '@shared/types/enums/features';
import FlavourConfig from '@shared/config/flavours';
import MenuData, { type MenuItem } from '@shared/components/menu/menuData';
import User from '@shared/classes/user';
import { useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import { routerSelector } from '@shared/store/slices/router.slice';

/**
 * Hook that retriebes the updated menu data and state.
 * @returns {Object} The updated menu data and state.
 */
export default function useUpdateMenu() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Selectors
  const { route } = useAppSelector(routerSelector);
  const { initialized, operator, planId } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  // Returns filtered menu items based on the plan and operator.
  return useMemo(() => {
    const plan = User.getPlan(planId);
    const items: MenuItem[] = [];
    Object.values(MenuData).map((item) => {
      let addItem = true;
      switch (item) {
        case MenuData.Movies:
        case MenuData.Series:
        case MenuData.Shows:
          addItem = !FlavourConfig.capabilities.anonymous
            ? plan?.features?.includes(Features.LAST_SHOWS) || false
            : true;
          break;
        case MenuData.Recordings:
          addItem =
            !(operator?.mySpaceEnabled || false) &&
            (!FlavourConfig.capabilities.anonymous
              ? plan?.features?.includes(Features.ALLOW_RECORD) || false
              : true);
          break;
        case MenuData.MoreContent:
          addItem = FlavourConfig.capabilities.anonymous
            ? operator?.moreContentEnabled || false
            : false;
          break;
        case MenuData.MySpace:
          addItem = FlavourConfig.capabilities.anonymous
            ? operator?.mySpaceEnabled || false
            : false;
          break;
      }
      addItem && items.push(item);
    });
    return items;
  }, [initialized, operator, planId, route, MenuData]);
}
